import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Button, Card, CardBody, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, Row, UncontrolledDropdown } from 'reactstrap'
import AppPermissionsSwitcher from '../../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../../Components/AppPermissionsSwitcher/constants/actions'
import PageTitle from '../../../Components/Common/PageTitle'
import { DeleteModalProps, setupDeleteCallback } from '../../../Components/Modals/DeleteModal/utils'
import QueryErrorBoundary from '../../../Components/QueryErrorBoundary/QueryErrorBoundary'
import { DataTableLayout, RenderRowProps, useDataTable, useRowClick, useRowPermission, useTableState } from '../../../Components/TanstackTable'
import contacts from '../../../api/contacts/hooks'
import { Contact, ContactCriteria } from '../../../api/contacts/types'
import { ContactType, History, PageListParams, PermissionsProps } from '../../../api/globalTypes'
import { MODAL_TYPES } from '../../../common/constants'
import { showModal } from '../../../store/modal'
import { selectSearchCriteriaContacts } from '../../../store/search'
import { selectCheckedTableRows } from '../../../store/table'
import useTableColumns from '../hooks/useTableColumns/useTableColumns'
import { getFilteredContact } from '../utils'
import { getModalPayloads } from './listModalPayloads'

const ContactList = () => {
    const history: History = useHistory()
    const { t } = useTranslation()
    const contactSearchCriteria = useSelector(selectSearchCriteriaContacts) as Partial<ContactCriteria>

    const tableState = useTableState({
        tableKey: 'contact',
        initialPage: history.location.state?.page,
    })

    const queryParams = {
        ...tableState.params,
        criteria: {
            ...contactSearchCriteria,
            contactType: ContactType.Person,
        },
    }

    return (
        <div className="page-content">
            <Container fluid>
                <PageTitle title={t('app.common.contacts')} />
                <Row>
                    <Col lg={12}>
                        <Card id="RealEstatesList">
                            <CardBody>
                                <QueryErrorBoundary resetKeys={[JSON.stringify(queryParams)]}>
                                    <ContactTable tableState={tableState} queryParams={queryParams} />
                                </QueryErrorBoundary>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

interface ContactTableProps {
    tableState: ReturnType<typeof useTableState>
    queryParams: PageListParams<ContactCriteria>
}

const ContactTable = ({ tableState, queryParams }: ContactTableProps) => {
    const location = useLocation()
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { DELETE_MODAL, CHANGE_AGENT_MODAL, EXPORT_DATA_MODAL } = MODAL_TYPES

    const selectedTableRows = useSelector(selectCheckedTableRows)

    const getContactsConfig = {
        params: { ...queryParams },
        isEnabled: { enabled: Boolean(tableState.params.page && tableState.params.limit), throwOnError: true },
    }

    const { data: contactsData, isLoading } = contacts.GetContacts.useQuery(getContactsConfig.params, getContactsConfig.isEnabled)
    const { mutate: deleteContacts } = contacts.DeleteContacts.useMutation()

    const contactItems = useMemo(() => contactsData?.items ?? [], [contactsData?.items])

    const onRowClick = useRowClick('/contacts')
    const columns = useTableColumns()
    const enableRow = useRowPermission<Contact>('contacts', action.delete)

    const { table, updateColumnSizing } = useDataTable({
        ...tableState,
        columns,
        data: contactItems,
        pageCount: contactsData?.pagination?.totalPages,
        selectRow: {
            mapRow: getFilteredContact,
            enableRow,
        },
    })

    const handleModal = (modalType: string) => {
        const payload = getModalPayloads({
            modalType,
            selectedTableRows,
            params: queryParams,
            columns,
        })

        if (!payload) return

        setupDeleteCallback(modalType, payload.modalProps as DeleteModalProps, (ids) => deleteContacts({ ids: ids as number[] }))
        dispatch(showModal(payload))
    }

    const tableActions = () => (
        <div className="d-flex flex-grow-1">
            <Button tag={Link as React.ElementType} to="/contacts/new" color="add" className="btn-label">
                <i className="mdi mdi-account-outline align-bottom label-icon align-middle fs-16 me-2"></i>
                {t('button.add')}
            </Button>
            <UncontrolledDropdown className="ms-2">
                <DropdownToggle title={t('table.showMore')} href="#" className="btn btn-primary dropdown" tag="button">
                    <i className="mdi mdi-dots-horizontal align-middle"></i>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                    <DropdownItem onClick={() => handleModal(DELETE_MODAL)} disabled={!selectedTableRows.length}>
                        <i className="mdi mdi-trash-can-outline align-bottom me-2 text-muted"></i> {t('table.action.deleteSelected')}
                    </DropdownItem>
                    <AppPermissionsSwitcher permission="ChangeAgent">
                        {({ hasPermission }: { hasPermission: boolean }) =>
                            hasPermission && (
                                <DropdownItem
                                    onClick={() => handleModal(CHANGE_AGENT_MODAL)}
                                    disabled={!selectedTableRows.length}
                                >
                                    <i className="mdi mdi-account-switch-outline align-bottom me-2 text-muted"></i> {t('app.common.switchAgent')}
                                </DropdownItem>
                            )
                        }
                    </AppPermissionsSwitcher>
                    <AppPermissionsSwitcher permission="ExportData">
                        {({ hasPermission }: { hasPermission: boolean }) =>
                            hasPermission && (
                                <DropdownItem onClick={() => handleModal(EXPORT_DATA_MODAL)}>
                                    <i className="mdi mdi-export align-bottom me-2 text-muted"></i> {t('app.common.exportData.title')}
                                </DropdownItem>
                            )
                        }
                    </AppPermissionsSwitcher>
                </DropdownMenu>
            </UncontrolledDropdown>
        </div>
    )

    const handlePermissions = ({ row, children }: RenderRowProps<Contact>) => {
        const agent = row.original.agent

        return (
            <AppPermissionsSwitcher
                key={row.id}
                permission="ModulePermissions"
                itemAgentId={agent?.id}
                itemAgentOfficeId={agent?.agencyOffice?.id}
                module={location}
                action={action.view}
            >
                {({ hasModulePermission }: PermissionsProps) =>
                    children({
                        onClick: (e: React.MouseEvent<HTMLElement>) => hasModulePermission && onRowClick(row.original.id, e, tableState.params.page),
                        style: { cursor: hasModulePermission ? 'pointer' : 'not-allowed' },
                    })
                }
            </AppPermissionsSwitcher>
        )
    }

    return (
        <DataTableLayout
            table={table}
            isLoading={isLoading}
            totalItems={contactsData?.pagination?.totalItems}
            hasColumnSelection
            hasPageSizeSelect
            theadClass="dmn-table-header"
            toolbarActions={tableActions}
            updateColumnSizing={updateColumnSizing}
            renderRow={handlePermissions}
        />
    )
}

export default ContactList
